import { useRef, useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { addPositiveSymbol } from './symbol_utils'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { Tune, Visibility, VisibilityOff } from '@mui/icons-material'
import { Button } from '@mui/material'
import PartReferencePresetForm from './PartReferencePresetForm'

const PartReferencePreset = ({ partReferenceId }) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [openForm, setOpenForm] = useState(false)
  const [forceLoad, setForceLoad] = useState(false)
  const [hideTolerances, setHideTolerances] = useState(false)
  const formRef = useRef(null)

  const variables = ['x', 'y', 'z', 'a', 'b', 'c']

  const validateUpperTolerance = (row, variable, upperCheckMode) => {
    const upper = row[variable + 'valueUpperTolerance']
    const lower = row[variable + 'valueLowerTolerance']
    const value = row[variable + 'value']
    const current = upperCheckMode ? upper : lower

    if (value === '' || value === null) {
      // Value is not defined so it should not be defined the lower and upper
      if (current === null || current === '') {
        return
      }
      return t_('Should be empty')
    }

    const currentNum = parseFloat(current)
    if (isNaN(currentNum)) {
      return t_('Is not a number')
    }

    // Check value is valid
    const upperNum = parseFloat(upper)
    if (isNaN(upperNum)) {
      return
    }
    const lowerNum = parseFloat(lower)
    if (isNaN(lowerNum)) {
      return
    }
    if (upperNum <= lowerNum) {
      return t_('Upper tolerance must be higher than lower tolerance')
    }
  }

  const variablesColumns = variables.flatMap((variable) => [
    {
      name: t_(variable.toUpperCase()),
      field: variable + 'value',
      numeric: true,
      condition: (row) => undefined
    },
    {
      name: variable.toUpperCase() + ' ' + t_('Upper tolerance'),
      field: variable + 'valueUpperTolerance',
      hide: hideTolerances,
      condition: (row) => validateUpperTolerance(row, variable, true)
    },
    {
      name: variable.toUpperCase() + ' ' + t_('Lower tolerance'),
      field: variable + 'valueLowerTolerance',
      hide: hideTolerances,
      condition: (row) => validateUpperTolerance(row, variable, false)
    }
  ])

  const columns = [
    {
      name: t_('Fixture Reference'),
      field: 'fixtureReference.code',
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('fixtureReferencesByPartReference/' + partReferenceId)
        },
        id: 'id',
        field: 'code'
      },
      mandatory: true
    },
    {
      name: t_('Preset number'),
      field: 'presetNumber',
      numeric: true
    },
    ...variablesColumns
  ]

  const dataFetch = async (pagination) => {
    const data = await apiGet('fixtureReferencePartReferencePreset/' + partReferenceId, pagination)
    data.content.forEach((row) => {
      variables.forEach((variable) => {
        row[variable + 'valueUpperTolerance'] = addPositiveSymbol(row[variable + 'valueUpperTolerance'])
        row[variable + 'valueLowerTolerance'] = addPositiveSymbol(row[variable + 'valueLowerTolerance'])
      })
    })
    return data
  }

  const actions = {
    create: {
      fetchFunction: (row) => {
        row.partReference = partReferenceId
        return apiPost('fixtureReferencePartReferencePreset', row)
      },
      disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_PRESET_ADD')
    },
    edit: {
      fetchFunction: (row) => {
        row.partReference = partReferenceId
        return apiPut('fixtureReferencePartReferencePreset', row)
      },
      disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_PRESET_EDIT')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('fixtureReferencePartReferencePreset/' + rows[0].id)
        } else {
          return apiDelete('fixtureReferencePartReferencePreset/' + partReferenceId + '/deleteMulti', rows)
        }
      },
      disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_PRESET_DELETE')
    }

  }

  return (
    <>
      <SmartDialog
        closeCallback={() => setOpenForm(false)}
        accept
        acceptLabel='Save'
        acceptCallback={() => formRef.current?.submit()}
        cancel={false}
        close
        title={createDialogTitle({ color: 'primary', title: t_('Add preset'), icon: Tune })}
        renderComponent={
          <PartReferencePresetForm
            ref={formRef}
            partReferenceId={partReferenceId}
            onClose={() => setOpenForm(false)}
            onSaved={() => setForceLoad((previous) => !previous)}
          />
        }
        setOpen={openForm}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='features'
        columns={columns}
        dataFetch={dataFetch}
        actions={actions}
        multipleSelection
        pageable
        forceLoad={forceLoad}
        onAddElementClick={() => setOpenForm(true)}
        customAddRecordClick
        toolbar={{
          renderSticky: {
            fetchFunction: () => (
              <Button
                variant='text'
                color='primary'
                startIcon={hideTolerances ? <Visibility /> : <VisibilityOff />}
                onClick={() => setHideTolerances((previous) => !previous)}
              >
                {hideTolerances ? t_('Show tolerances') : t_('Hide tolerances')}
              </Button>
            )
          }
        }}
      />
    </>
  )
}

export default PartReferencePreset
